import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { User } from '../interfaces/user.interface';
import { environment } from '../../../environments/environment';

function isTokenExpired(token: string): boolean {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('JWT must have 3 parts');
  }
  let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  switch (base64.length % 4) {
    case 2:
      base64 += '==';
      break;
    case 3:
      base64 += '=';
      break;
    default:
      break;
  }
  const decoded = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );
  const payload = JSON.parse(decoded);
  if (!payload.exp) {
    return false;
  }
  return Date.now() >= payload.exp * 1000;
}

function tokenNotExpired(tokenName: string): boolean {
  const token = localStorage.getItem(tokenName);
  return token != null && !isTokenExpired(token);
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public authToken: string | null;

  public user: User;

  public headers: HttpHeaders;

  constructor(public http: HttpClient) {
    this.headers = new HttpHeaders().set('Content-Type', 'application/json');
    this.user = JSON.parse(localStorage.getItem('usuario')) || {};
  }

  /* eslint-disable @typescript-eslint/no-explicit-any --
     API genérica del backend: el payload y la forma de la respuesta cambian según
     el endpoint que la llame en cada punto de uso; no hay un tipo único posible aquí. */

  /**
   * API Generico GET componente
   * @param {string} api route: api/componente
   * @return {Observable<any>} respuesta asincrónica
   */
  get2(object: string) {
    this.headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', this.getToken());
    return this.http.get<any>(`${environment.baseURL}${object}`, {
      headers: this.headers,
    });
  }

  /**
   * API Generico POST
   * @param {string} api route: api/componente
   * @param {any} data objeto a persistir
   * @return {Observable<any>} respuesta asincrónica
   */
  post2(object: string, data: any) {
    this.headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', this.getToken());
    return this.http.post<any>(`${environment.baseURL}${object}`, JSON.stringify(data), { headers: this.headers });
  }

  post({ api, data }: { api: string; data: any }) {
    return this.http.post<any>(`${environment.baseURL}${api}`, JSON.stringify(data), { headers: this.headers });
  }

  /**
   * API Generico DELETE
   * @param {string} api route: api/componente/id
   * @return {Observable<any>} respuesta asincrónica
   */
  delete2(object: string) {
    this.headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', this.getToken());
    return this.http.delete<any>(`${environment.baseURL}${object}`, {
      headers: this.headers,
    });
  }

  public authenticateUser(user) {
    return this.http.post<any>(`${environment.baseURL}users/authenticate`, JSON.stringify(user), {
      headers: this.headers,
    });
  }

  /**
   * Arranca el enrolamiento de TOTP. `authToken` es el `setupToken` que devuelve
   * /authenticate cuando el usuario todavía no tiene MFA configurado (o, si ya está
   * logueado, su JWT de sesión normal) — va en el header, no en el body.
   */
  public setupTotp(authToken: string) {
    const headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', authToken);
    return this.http.post<any>(`${environment.baseURL}users/setup-totp`, JSON.stringify({}), { headers });
  }

  /**
   * Confirma el primer código TOTP. `authToken` es el mismo usado en setupTotp;
   * `totpSetupToken` es el `setupToken` que devolvió esa llamada (va en el body, es
   * un token distinto — lleva el secreto pendiente de confirmar).
   */
  public confirmTotp(authToken: string, totpSetupToken: string, code: string) {
    const headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', authToken);
    return this.http.post<any>(
      `${environment.baseURL}users/confirm-totp`,
      JSON.stringify({ setupToken: totpSetupToken, token: code }),
      { headers }
    );
  }

  public sendOtp(tempToken: string) {
    return this.http.post<any>(`${environment.baseURL}users/send-otp`, JSON.stringify({ tempToken }), {
      headers: this.headers,
    });
  }

  public verifyOtp(tempToken: string, code: string, method: string) {
    return this.http.post<any>(
      `${environment.baseURL}users/verify-otp`,
      JSON.stringify({ tempToken, code, method }),
      { headers: this.headers }
    );
  }

  public change(email, password) {
    const obj = { email, password };
    this.headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', this.getToken());
    return this.http.post<any>(`${environment.baseURL}users/reset`, JSON.stringify(obj), { headers: this.headers });
  }

  /**
   * API Generico PUT
   * @param {string} api route: api/componente/id
   * @param {any} data propiedades a actualizar
   * @return {Observable<any>} respuesta asincrónica
   */
  put2(object: string, data: any) {
    this.headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', this.getToken());
    return this.http.put<any>(`${environment.baseURL}${object}`, JSON.stringify(data), { headers: this.headers });
  }

  /* eslint-enable @typescript-eslint/no-explicit-any */

  public storeUserData(token: string, user: User) {
    localStorage.setItem('id_token', token);
    localStorage.setItem('usuario', JSON.stringify(user));
    this.authToken = token;
    this.user = user;
    return true;
  }

  public forgot(email) {
    const obj = { email };
    // API genérica del backend, ver comentario más arriba.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.post<any>(`${environment.baseURL}users/forgot`, JSON.stringify(obj), { headers: this.headers });
  }

  public loadToken() {
    const token = localStorage.getItem('id_token');
    this.authToken = token;
  }

  public logout() {
    this.authToken = null;
    this.user = {};
    localStorage.clear();
  }

  public getToken() {
    this.loadToken();
    return this.authToken || '';
  }

  public static loggedIn() {
    return tokenNotExpired('id_token');
  }

  public getUser() {
    return this.user;
  }
}
